/**
 * Two-stage table filter with saved presets (CC_05 Block 5). Up to two chained
 * conditions combined with AND/OR, applied client-side (table-filter.ts); named
 * presets persist per user+scope via the saved-views API. Reusable across the
 * files / library / list tables — the caller passes the available fields and a
 * `scope` string that keeps each table's presets apart.
 */

import { useEffect, useState } from "react";

import { ryfeApi } from "../api";
import {
  EMPTY_FILTER,
  type FilterConfig,
  type FilterOp,
} from "../table-filter";

import "./ryfe.scss";

import type { SavedView } from "../api";

export interface FilterField {
  key: string;
  label: string;
  /** Spaltentyp (text/select/number/date/url/…) — steuert den Wert-Editor. */
  type?: string;
  /** Für select-Spalten: die Optionen. Wert = gespeicherter Zell-Wert. */
  options?: { value: string; label: string }[];
}

const OPS: { op: FilterOp; label: string; needsValue: boolean }[] = [
  { op: "contains", label: "enthält", needsValue: true },
  { op: "eq", label: "ist gleich", needsValue: true },
  { op: "neq", label: "ist ungleich", needsValue: true },
  { op: "gt", label: "größer als", needsValue: true },
  { op: "lt", label: "kleiner als", needsValue: true },
  { op: "notEmpty", label: "ist gesetzt", needsValue: false },
  { op: "empty", label: "ist leer", needsValue: false },
];

const opNeedsValue = (op: FilterOp) =>
  OPS.find((o) => o.op === op)?.needsValue ?? true;

export const FilterBar = ({
  scope,
  fields,
  config,
  onChange,
}: {
  scope: string;
  fields: FilterField[];
  config: FilterConfig;
  onChange: (c: FilterConfig) => void;
}) => {
  const [views, setViews] = useState<SavedView[]>([]);
  const [open, setOpen] = useState(false);

  const loadViews = async () => {
    try {
      const { views: v } = await ryfeApi.listSavedViews(scope);
      setViews(v);
    } catch {
      /* presets are optional */
    }
  };
  useEffect(() => {
    loadViews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  const conds = config.conditions;

  const setCond = (
    i: number,
    patch: Partial<FilterConfig["conditions"][0]>,
  ) => {
    const next = conds.slice();
    next[i] = { ...next[i], ...patch };
    onChange({ ...config, conditions: next });
  };
  const addCond = () =>
    onChange({
      ...config,
      conditions: [
        ...conds,
        { field: fields[0]?.key ?? "", op: "contains", value: "" },
      ],
    });
  const removeCond = (i: number) =>
    onChange({ ...config, conditions: conds.filter((_, j) => j !== i) });
  const clearAll = () => onChange(EMPTY_FILTER);

  const saveCurrent = async () => {
    const name = window.prompt("Name für diesen Filter");
    if (!name) {
      return;
    }
    try {
      await ryfeApi.createSavedView(scope, name, config);
      await loadViews();
    } catch {
      /* ignore */
    }
  };
  const applyView = (v: SavedView) => {
    const c = v.config as FilterConfig;
    if (c && Array.isArray(c.conditions)) {
      onChange({
        combinator: c.combinator === "or" ? "or" : "and",
        conditions: c.conditions,
      });
    }
  };
  const removeView = async (id: string) => {
    try {
      await ryfeApi.deleteSavedView(id);
      await loadViews();
    } catch {
      /* ignore */
    }
  };

  const activeCount = conds.filter(
    (c) => c.field && (!opNeedsValue(c.op) || c.value),
  ).length;

  return (
    <div className="ryfe-filterbar">
      <button
        className="ryfe-btn ryfe-btn--ghost"
        onClick={() => setOpen((o) => !o)}
        title="Filtern"
      >
        ⚟ Filter{activeCount > 0 ? ` (${activeCount})` : ""}
      </button>

      {views.length > 0 && (
        <select
          className="ryfe-field ryfe-filterbar__preset"
          value=""
          onChange={(e) => {
            const v = views.find((x) => x.id === e.target.value);
            if (v) {
              applyView(v);
              setOpen(true);
            }
          }}
        >
          <option value="">Gespeicherte Filter…</option>
          {views.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      )}

      {open && (
        <div className="ryfe-filterbar__panel">
          {conds.length === 0 && (
            <div className="ryfe-muted" style={{ padding: 0 }}>
              Keine Bedingung. „+ Bedingung" fügt eine hinzu (max. zwei).
            </div>
          )}
          {conds.map((c, i) => (
            <div className="ryfe-filterbar__row" key={i}>
              {i > 0 && (
                <select
                  className="ryfe-field"
                  value={config.combinator}
                  onChange={(e) =>
                    onChange({
                      ...config,
                      combinator: e.target.value === "or" ? "or" : "and",
                    })
                  }
                >
                  <option value="and">UND</option>
                  <option value="or">ODER</option>
                </select>
              )}
              <select
                className="ryfe-field"
                value={c.field}
                onChange={(e) => setCond(i, { field: e.target.value })}
              >
                {fields.map((f) => (
                  <option key={f.key} value={f.key}>
                    {f.label}
                  </option>
                ))}
              </select>
              <select
                className="ryfe-field"
                value={c.op}
                onChange={(e) => setCond(i, { op: e.target.value as FilterOp })}
              >
                {OPS.map((o) => (
                  <option key={o.op} value={o.op}>
                    {o.label}
                  </option>
                ))}
              </select>
              {opNeedsValue(c.op) &&
                (() => {
                  const field = fields.find((f) => f.key === c.field);
                  // select-Spalten: der sichtbare Wert ist das Label, gefiltert
                  // wird aber der gespeicherte Options-Wert. Ohne Dropdown müsste
                  // der Nutzer die interne id raten → leere Treffermenge (P5-Bug).
                  if (
                    field?.type === "select" &&
                    field.options &&
                    field.options.length > 0
                  ) {
                    return (
                      <select
                        className="ryfe-field"
                        value={c.value ?? ""}
                        onChange={(e) => setCond(i, { value: e.target.value })}
                      >
                        <option value="">— Wert —</option>
                        {field.options.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    );
                  }
                  const inputType =
                    field?.type === "date"
                      ? "date"
                      : field?.type === "number"
                      ? "number"
                      : "text";
                  return (
                    <input
                      className="ryfe-field"
                      type={inputType}
                      placeholder="Wert"
                      value={c.value ?? ""}
                      onChange={(e) => setCond(i, { value: e.target.value })}
                    />
                  );
                })()}
              <button
                className="ryfe-btn ryfe-btn--ghost"
                onClick={() => removeCond(i)}
                title="Bedingung entfernen"
              >
                ✕
              </button>
            </div>
          ))}

          <div className="ryfe-filterbar__actions">
            {conds.length < 2 && (
              <button className="ryfe-btn ryfe-btn--ghost" onClick={addCond}>
                + Bedingung
              </button>
            )}
            <button className="ryfe-btn ryfe-btn--ghost" onClick={clearAll}>
              Zurücksetzen
            </button>
            <span className="ryfe-topbar__spacer" />
            <button
              className="ryfe-btn ryfe-btn--ghost"
              onClick={saveCurrent}
              disabled={activeCount === 0}
            >
              Filter speichern
            </button>
          </div>

          {views.length > 0 && (
            <div className="ryfe-filterbar__saved">
              {views.map((v) => (
                <span key={v.id} className="ryfe-chip ryfe-chip--filter">
                  <button
                    className="ryfe-linkish"
                    onClick={() => applyView(v)}
                    title="Anwenden"
                  >
                    {v.name}
                  </button>
                  <button
                    className="ryfe-filterbar__del"
                    onClick={() => removeView(v.id)}
                    title="Löschen"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
